/**
 * Flows API Client
 * Fetches WhatsApp flow definitions, conversation counts, and funnel data.
 */

import { apiRequest } from './base-client';

// Types
export interface FlowSummary {
  id: string;
  slug: string;
  name: string;
  namePtBr: string | null;
  description: string;
  descriptionPtBr: string | null;
  isActive: boolean;
  steps: number;
  createdAt: string;
}

export interface FlowNode {
  id: string;
  slug: string;
  name: string;
  namePtBr: string | null;
  nodeType: string;
  position: number;
  messageText: string | null;
  messageTextPtBr: string | null;
  templateSlug: string | null;
}

export interface FlowDetail extends Omit<FlowSummary, 'steps'> {
  nodes: FlowNode[];
}

interface FlowListResponse {
  success: boolean;
  flows: FlowSummary[];
}

interface FlowDetailResponse {
  success: boolean;
  flow: FlowDetail;
}

interface FlowCountsResponse {
  success: boolean;
  flowSlug: string;
  counts: Record<string, number>;
  total: number;
}

export interface FunnelStage {
  stage: string;
  displayName: string;
  count: number;
  percentage: number;
  dropOff: number;
  conversionRate: number;
}

interface FlowFunnelResponse {
  success: boolean;
  flowSlug: string;
  funnel: FunnelStage[];
  totalConversations: number;
  overallConversionRate: number;
}

/** Fetch all active flows for a business */
export async function getFlows(businessId: string): Promise<FlowSummary[]> {
  const data = await apiRequest<FlowListResponse>('/api/operator/flows', {
    params: { businessId },
  });
  return data.flows;
}

/** Fetch a single flow with its nodes */
export async function getFlow(flowSlug: string, businessId: string): Promise<FlowDetail> {
  const data = await apiRequest<FlowDetailResponse>(`/api/operator/flows/${flowSlug}`, {
    params: { businessId },
  });
  return data.flow;
}

/** Fetch conversation counts per stage for a flow */
export async function getFlowCounts(flowSlug: string, businessId: string): Promise<Record<string, number>> {
  const data = await apiRequest<FlowCountsResponse>(`/api/operator/flows/${flowSlug}/counts`, {
    params: { businessId },
  });
  return data.counts;
}

/** Fetch funnel analytics for a flow */
export async function getFlowFunnel(
  flowSlug: string,
  businessId: string,
  dateRange?: { startDate?: string; endDate?: string }
): Promise<{ funnel: FunnelStage[]; totalConversations: number; overallConversionRate: number }> {
  const data = await apiRequest<FlowFunnelResponse>(`/api/operator/flows/${flowSlug}/funnel`, {
    params: { businessId, ...dateRange },
  });
  return {
    funnel: data.funnel,
    totalConversations: data.totalConversations,
    overallConversionRate: data.overallConversionRate,
  };
}
